import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { showToast } from "../helper/notifications";
import { Icon } from "@iconify/react";
import { GoogleMap, MarkerF } from "@react-google-maps/api";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import PageHeader from "../components/common/PageHeader";
import BusinessHoursSelector from "../components/common/BusinessHoursSelector";

const MAP_CONTAINER_STYLE = { width: "100%", height: "500px", borderRadius: "12px" };

const GooglePlaceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoaded } = useGoogleMaps();

  const mapRef = useRef(null);
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  const [formData, setFormData] = useState({
    place_id: "",
    name: "",
    formatted_address: "",
    location_lat: 0,
    location_lng: 0,
    business_status: "OPERATIONAL",
    rating: "",
    user_ratings_total: 0,
    price_level: 0,
    website_uri: "",
    phone_number: "",
    editorial_summary: "",
    opening_hours_json: {},
    reviews_json: [],
    photos_json: [],
    delivery: 0, dine_in: 0, takeout: 0, reservable: 0,
    serves_beer: 0, serves_wine: 0, serves_breakfast: 0, 
    serves_dinner: 0, serves_lunch: 0, serves_vegetarian: 0
  });

  // 1. Map Load Handler
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // 2. Fetch Logic
  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await api.get(`/google-places`);
        const list = res.data.data || res.data; 
        const data = list.find(p => p.place_id === id);

        if (data) {
          const safeParse = (val, fallback) => {
            if (!val) return fallback;
            if (typeof val === "object") return val;
            try { return JSON.parse(val); } catch (e) { return fallback; }
          };

          const parsedHours = safeParse(data.opening_hours_json, {});
          
          setFormData({
            ...data,
            name: data.name || "",
            formatted_address: data.formatted_address || "",
            rating: data.rating || "0",
            phone_number: data.phone_number || "",
            website_uri: data.website_uri || "",
            editorial_summary: data.editorial_summary || "",
            opening_hours_json: parsedHours.weekday_text ? formatForSelector(parsedHours.weekday_text) : {},
            reviews_json: safeParse(data.reviews_json, []),
            photos_json: safeParse(data.photos_json, []),
            location_lat: parseFloat(data.location_lat) || 0,
            location_lng: parseFloat(data.location_lng) || 0
          });
        }
      } catch (err) {
        showToast("error", "Failed to load place data");
      } finally {
        setLoading(false);
      }
    };

    const formatForSelector = (weekdayText) => {
      const result = {};
      const dayMap = { "Monday": "monday", "Tuesday": "tuesday", "Wednesday": "wednesday", "Thursday": "thursday", "Friday": "friday", "Saturday": "saturday", "Sunday": "sunday", "Montag": "monday", "Dienstag": "tuesday", "Mittwoch": "wednesday", "Donnerstag": "thursday", "Freitag": "friday", "Samstag": "saturday", "Sonntag": "sunday" };
      
      weekdayText.forEach(line => {
        const [day, time] = line.split(": ");
        const key = dayMap[day] || day.toLowerCase();
        result[key] = time === "Geschlossen" || time === "Closed" ? "Closed" : time.replace("–", "-").replace(" Uhr", "");
      });
      return result;
    };

    if (id) loadData();
  }, [id]);

  // 3. Sync map center when coordinates change manually
  useEffect(() => {
    if (mapRef.current && formData.location_lat && formData.location_lng) {
      mapRef.current.panTo({ lat: formData.location_lat, lng: formData.location_lng });
    }
  }, [formData.location_lat, formData.location_lng]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : (name === "location_lat" || name === "location_lng" ? parseFloat(value) : value),
    }));
  };

  const onMarkerDragEnd = (e) => {
    setFormData((prev) => ({
      ...prev,
      location_lat: e.latLng.lat(),
      location_lng: e.latLng.lng()
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    try {
      const payload = {
        ...formData,
        opening_hours_json: JSON.stringify({
          weekday_text: Object.entries(formData.opening_hours_json).map(([d, t]) => 
            `${d.charAt(0).toUpperCase() + d.slice(1)}: ${t === "Closed" ? "Geschlossen" : t + " Uhr"}`
          )
        }),
        reviews_json: JSON.stringify(formData.reviews_json),
        photos_json: JSON.stringify(formData.photos_json),
      };
      
      await api.put(`/google-places/${id}`, payload);
      
      // REPLACED: toast.success -> showToast('success', ...)
      showToast("success", "Place updated successfully");
      
      navigate("/locations-google");
    } catch (err) {
      // REPLACED: toast.error -> showToast('error', ...)
      showToast("error", "Update failed");
    } finally {
      setSaveLoading(false);
    }
};

  if (loading) return <div className="p-40 text-center"><Icon icon="line-md:loading-twotone-loop" className="text-4xl text-primary-600" /></div>;

  return (
    <>
      <PageHeader title={formData.name || "Edit Location"} subtitle={`Place ID: ${formData.place_id}`} />

      {/* Tabs Menu */}
      <ul className="nav nav-pills mb-24 gap-2 border-0">
        {['general', 'location', 'amenities', 'hours', 'reviews', 'photos'].map(tabId => (
          <li className="nav-item" key={tabId}>
            <button
              type="button"
              className={`nav-link radius-8 px-20 py-12 text-capitalize fw-bold ${activeTab === tabId ? 'active btn-primary-600 shadow-sm' : 'bg-base border text-secondary-light'}`}
              onClick={() => setActiveTab(tabId)}
            >
              {tabId}
            </button>
          </li>
        ))}
      </ul>

      <div className="card radius-12 border-0 bg-base shadow-none">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              
              <div className={activeTab === 'location' ? 'col-lg-5' : 'col-12'}>
                {activeTab === "general" && (
                  <div className="row g-4">
                    <div className="col-md-12">
                      <label className="form-label fw-bold">Business Name</label>
                      <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Phone Number</label>
                      <input type="text" className="form-control" name="phone_number" value={formData.phone_number} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Rating</label>
                      <input type="text" className="form-control" name="rating" value={formData.rating} onChange={handleChange} />
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-bold">Website URI</label>
                      <input type="text" className="form-control" name="website_uri" value={formData.website_uri} onChange={handleChange} />
                    </div>
                  </div>
                )}

                {activeTab === "location" && (
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label fw-bold">Formatted Address</label>
                      <textarea className="form-control" rows="2" name="formatted_address" value={formData.formatted_address} onChange={handleChange} />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold text-primary-600">Latitude</label>
                      <input type="number" step="any" className="form-control fw-bold" name="location_lat" value={formData.location_lat} onChange={handleChange} />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold text-primary-600">Longitude</label>
                      <input type="number" step="any" className="form-control fw-bold" name="location_lng" value={formData.location_lng} onChange={handleChange} />
                    </div>
                    <div className="col-12 mt-12">
                      <div className="alert alert-info py-2 px-3 small d-flex align-items-center gap-2 mb-0">
                        <Icon icon="lucide:info" />
                        <span>Map is interactive. Drag the marker to adjust coordinates.</span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "hours" && (
                  <BusinessHoursSelector 
                    value={formData.opening_hours_json} 
                    onChange={(val) => setFormData({...formData, opening_hours_json: val})} 
                  />
                )}

                {activeTab === "amenities" && (
                  <div className="row g-3">
                    {[
                      { key: 'delivery', label: 'Delivery' },
                      { key: 'dine_in', label: 'Dine In' },
                      { key: 'takeout', label: 'Takeout' },
                      { key: 'reservable', label: 'Reservable' },
                      { key: 'serves_beer', label: 'Serves Beer' },
                      { key: 'serves_wine', label: 'Serves Wine' },
                      { key: 'serves_breakfast', label: 'Serves Breakfast' },
                      { key: 'serves_lunch', label: 'Serves Lunch' },
                      { key: 'serves_dinner', label: 'Serves Dinner' },
                      { key: 'serves_vegetarian', label: 'Serves Vegetarian' }
                    ].map((item) => (
                      <div className="col-md-4 col-sm-6" key={item.key}>
                        <div className="d-flex align-items-center justify-content-between p-16 border radius-12 bg-neutral-50 shadow-sm">
                          <span className="text-capitalize fw-bold text-sm text-secondary-light">{item.label}</span>
                          <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" name={item.key} checked={formData[item.key] === 1} onChange={handleChange} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === "reviews" && (
                  <div className="d-flex flex-column gap-3">
                    {formData.reviews_json.length > 0 ? formData.reviews_json.map((rev, i) => (
                      <div key={i} className="p-16 border radius-8 shadow-sm bg-white">
                        <div className="d-flex align-items-center gap-2 mb-8">
                          <img 
                            src={rev.profile_photo_url} 
                            className="w-32-px h-32-px rounded-circle" 
                            alt={rev.author_name}
                            onError={e => e.target.src=`https://ui-avatars.com/api/?name=${rev.author_name}`} 
                          />
                          <span className="fw-bold text-sm">{rev.author_name}</span>
                          <span className="ms-auto badge bg-warning-focus text-warning-main">★ {rev.rating}</span>
                        </div>
                        <p className="text-sm text-secondary-light mb-0">{rev.text}</p>
                      </div>
                    )) : <p className="text-center py-20 text-muted">No reviews available.</p>}
                  </div>
                )}

                {activeTab === "photos" && (
                  <div className="row g-3">
                    {formData.photos_json.length > 0 ? formData.photos_json.map((photo, i) => (
                      <div className="col-md-3 col-sm-4" key={i}>
                        <div className="card radius-8 overflow-hidden bg-neutral-100 d-flex align-items-center justify-content-center border" style={{height: '120px'}}>
                           {/* Note: Google photos usually require a specific API call. Displaying placeholder. */}
                          <Icon icon="lucide:image" className="text-2xl text-neutral-400" />
                          <small className="position-absolute bottom-0 w-100 text-center bg-dark text-white opacity-75 py-1" style={{fontSize: '9px'}}>
                            {photo.width} x {photo.height}
                          </small>
                        </div>
                      </div>
                    )) : <p className="text-center py-20 text-muted">No photos available.</p>}
                  </div>
                )}
              </div>

              {activeTab === 'location' && (
                <div className="col-lg-7">
                   <div className="border radius-12 overflow-hidden shadow-sm sticky-top" style={{ top: '24px' }}>
                    {isLoaded ? (
                      <GoogleMap 
                        onLoad={onMapLoad}
                        mapContainerStyle={MAP_CONTAINER_STYLE} 
                        center={{ lat: formData.location_lat, lng: formData.location_lng }} 
                        zoom={16}
                        options={{ mapTypeControl: true, streetViewControl: false }}
                      >
                        <MarkerF 
                          position={{ lat: formData.location_lat, lng: formData.location_lng }} 
                          draggable 
                          onDragEnd={onMarkerDragEnd} 
                        />
                      </GoogleMap>
                    ) : (
                      <div className="bg-neutral-50 d-flex align-items-center justify-content-center" style={MAP_CONTAINER_STYLE}>
                        <p className="text-secondary-light">Initialising Map...</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="col-12 d-flex justify-content-end gap-3 mt-24 border-top pt-24">
                <button type="button" className="btn bg-neutral-100 text-secondary-light px-24 py-11 radius-8 fw-semibold" onClick={() => navigate("/locations-google")}>Cancel</button>
                <button type="submit" disabled={saveLoading} className="btn btn-primary-600 px-32 py-11 radius-8 fw-bold">
                  {saveLoading ? <Icon icon="line-md:loading-twotone-loop" className="me-2" /> : <Icon icon="lucide:check-circle" className="me-2" />}
                  Save Location
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default GooglePlaceForm;